import React, { useState, useEffect, useCallback } from 'react';
import { Select, Button, Space, Spin } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { makeGetCall } from '../../utils/helper';
import { fetchTemplates } from './documentsApi';

interface TemplatePanelProps {
  pacsOrdId: string | number;
  modality?: string;
  bodyPart?: string;
  onInsertTemplate: (templateId: number) => void;
}

export default function TemplatePanel({ pacsOrdId, modality, bodyPart, onInsertTemplate }: TemplatePanelProps) {
  const [bodyPartsList, setBodyPartsList] = useState<any[]>([]);
  const [bodyPartsLoading, setBodyPartsLoading] = useState(false);
  const [selectedBodyPart, setSelectedBodyPart] = useState<string | null>(bodyPart || null);

  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // Fetch body parts filtered by modality
  useEffect(() => {
    if (!modality) return;
    setBodyPartsLoading(true);
    makeGetCall('/body-parts')
      .then((res: any) => {
        const all = res.data?.data || [];
        const filtered = all.filter((bp: any) => bp.modality === modality);
        setBodyPartsList(filtered);
      })
      .catch(() => setBodyPartsList([]))
      .finally(() => setBodyPartsLoading(false));
  }, [modality]);

  // Auto-select body part from study metadata
  useEffect(() => {
    if (bodyPart && bodyPartsList.length > 0) {
      const match = bodyPartsList.find((bp: any) => bp.code === bodyPart || bp.value === bodyPart);
      if (match) setSelectedBodyPart(match.code || match.value);
    }
  }, [bodyPart, bodyPartsList]);

  // Fetch templates when body part is selected
  const loadTemplates = useCallback((bp: string | null) => {
    if (!modality || !bp) {
      setTemplates([]);
      return;
    }
    setTemplatesLoading(true);
    setSelectedTemplateId(null);
    // owner_scope 'self' => shared/default templates + the logged-in user's own
    fetchTemplates({ modality, bodypart: bp, owner_scope: 'self' })
      .then((res) => setTemplates(res.data?.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [modality]);

  // Split into the user's own ("My Templates") vs shared defaults. Only build
  // groups when the user actually has personal templates, so the dropdown looks
  // exactly as before for everyone else.
  const templateSelectOptions = React.useMemo(() => {
    const toOption = (t: any) => ({
      label: t.dt_display_name || t.dt_template_name,
      value: t.dt_id,
    });
    const mine = templates.filter((t) => t.dt_owner_user_id != null);
    const defaults = templates.filter((t) => t.dt_owner_user_id == null);
    if (mine.length === 0) {
      return defaults.map(toOption);
    }
    const groups: any[] = [{ label: 'My Templates', options: mine.map(toOption) }];
    if (defaults.length > 0) {
      groups.push({ label: 'Default Templates', options: defaults.map(toOption) });
    }
    return groups;
  }, [templates]);

  useEffect(() => {
    loadTemplates(selectedBodyPart);
  }, [selectedBodyPart, loadTemplates]);

  const handleLoadTemplate = () => {
    if (!selectedTemplateId) {
      message.warning('Select a template first');
      return;
    }
    onInsertTemplate(selectedTemplateId);
  };

  return (
    <Space size="small">
      <FileTextOutlined style={{ color: '#999' }} />
      <Select
        placeholder="Body part"
        size="small"
        style={{ width: 150 }}
        value={selectedBodyPart}
        onChange={(val) => setSelectedBodyPart(val)}
        loading={bodyPartsLoading}
        allowClear
        onClear={() => { setSelectedBodyPart(null); setTemplates([]); setSelectedTemplateId(null); }}
      >
        {bodyPartsList.map((bp: any) => (
          <Select.Option key={bp.code || bp.value} value={bp.code || bp.value}>
            {bp.label}
          </Select.Option>
        ))}
      </Select>
      <Select
        placeholder={selectedBodyPart ? (templatesLoading ? 'Loading...' : 'Select template') : 'Select body part first'}
        size="small"
        style={{ width: 200 }}
        value={selectedTemplateId}
        onChange={setSelectedTemplateId}
        disabled={!selectedBodyPart || templates.length === 0}
        loading={templatesLoading}
        allowClear
        options={templateSelectOptions}
      />
      <Button
        size="small"
        type="primary"
        onClick={handleLoadTemplate}
        disabled={!selectedTemplateId}
      >
        Load
      </Button>
    </Space>
  );
}
